import { type CentraLanguageCode } from "@/data/supported-languages"
import { TRANSLATED_ROUTE_TYPES } from "@/i18n/translations"
import {
    DETECTED_PAGE_TYPE,
    type DetectedPageType,
    ROUTE_SEGMENTS,
    type RouteSegment,
    type TranslatedRoutesKey
} from "@/i18n/types"
import { UNIVERSAL_DEFAULT_LANGUAGE } from "@/lib/siteconfig/constants"
import { cleanPathPattern } from "@/lib/siteconfig/regexPatterns"

export function getLangCodeFromLocale(
    locale: string
): CentraLanguageCode | typeof UNIVERSAL_DEFAULT_LANGUAGE {
    // Adjust if you use something else, but for "de" / "de-DE" this is fine
    return locale.slice(0, 2).toLowerCase() as
        | CentraLanguageCode
        | typeof UNIVERSAL_DEFAULT_LANGUAGE
}

/**
 * Converts a translated route segment back to its canonical form
 * e.g., "produit" -> "product", "catégories" -> "categories", "presse" -> "pressroom"
 *
 * This is the reverse of localizeSegment() and works across all languages
 * by searching through ROUTE_SEGMENT_TRANSLATIONS
 */
export function canonicalizeSegment(translatedSegment: string): string {
    // Check all language translations to find the canonical equivalent

    // Decode URL-encoded segments first
    let decodedSegment: string
    try {
        decodedSegment = decodeURIComponent(translatedSegment)
    } catch {
        // If decoding fails, use original (might already be decoded)
        decodedSegment = translatedSegment
    }
    // Check all language translations to find the canonical equivalent
    for (const [canonical, translations] of Object.entries(TRANSLATED_ROUTE_TYPES)) {
        // const canonical = translations[decodedSegment as keyof typeof translations]
        if (Object.values(translations).includes(decodedSegment)) {
            return canonical
        }
    }
    // If not found in translations, assume it's already canonical
    return decodedSegment
}

// Canonical -> localized segment, e.g. ("de", "product") => "produkt"
export function localizeSegment(locale: string, canonical: RouteSegment): string {
    const langCode = getLangCodeFromLocale(locale)
    if (langCode === UNIVERSAL_DEFAULT_LANGUAGE) return canonical

    const translationMap = TRANSLATED_ROUTE_TYPES[canonical as TranslatedRoutesKey]
    if (!translationMap) return canonical

    return translationMap[langCode as keyof typeof translationMap] ?? canonical
}

/**
 * Takes a canonical, root-relative href WITHOUT locale,
 * e.g. "/product/slug", "/categories/foo", and returns
 * a localized href WITHOUT locale:
 *   "/product/slug"   (en)
 *   "/produkt/slug"   (de)
 *   "/produit/slug"   (fr)
 */
export function localizeHrefForLocale(href: string, locale: string): string {
    // Only localize internal absolute paths
    if (!href.startsWith("/")) return href

    const segments = href.split("/") // ["", "product", "slug", ...]
    if (segments.length < 2) return href

    const canonicalSegment = segments[1] as RouteSegment

    if (!Object.values(ROUTE_SEGMENTS).includes(canonicalSegment)) {
        // "/about", "/some-random-page" – leave as is
        return href
    }

    const localized = localizeSegment(locale, canonicalSegment)
    segments[1] = encodeURIComponent(localized)

    return segments.join("/")
}

/**
 * Convenience: takes a canonical href WITHOUT locale
 * and returns a fully localized, locale-prefixed URL:
 *
 *   buildLocalizedUrl("de", "/product/slug")
 *   => "/de/produkt/slug"
 */
export function buildLocalizedUrl(locale: string, canonicalHref: string): string {
    const base = canonicalHref.startsWith("/") ? canonicalHref : `/${canonicalHref}`

    const localizedPath = localizeHrefForLocale(base, locale)

    return `/${locale}${localizedPath}`
}

/**
 * Detects the page type from a path, handling both canonical and translated route segments
 */
export function detectPageTypeFromPath(path: string): DetectedPageType | null {
    if (!path) return null

    // Decode URL-encoded path
    let decodedPath: string
    try {
        decodedPath = decodeURIComponent(path)
    } catch {
        decodedPath = path
    }

    const cleanPath = decodedPath.replace(cleanPathPattern, "")

    // Extract the first segment and normalize it to canonical form
    // This handles both canonical (e.g., "product") and translated (e.g., "produit") segments
    const firstSegment = cleanPath.split("/")[0]
    const normalizedSegment = canonicalizeSegment(firstSegment)

    // Check against canonical segments
    if (normalizedSegment === ROUTE_SEGMENTS.journal) {
        // Handle both "journal" and "journal/slug"
        if (cleanPath === firstSegment || cleanPath.startsWith(firstSegment + "/")) {
            return DETECTED_PAGE_TYPE.article
        }
    }

    if (normalizedSegment === ROUTE_SEGMENTS.pressroom) {
        // Handle both "pressroom" and "pressroom/slug"
        if (cleanPath === firstSegment || cleanPath.startsWith(firstSegment + "/")) {
            return DETECTED_PAGE_TYPE.pressroomArticle
        }
    }

    if (normalizedSegment === ROUTE_SEGMENTS.discover) {
        if (cleanPath === firstSegment || cleanPath.startsWith(firstSegment + "/")) {
            return DETECTED_PAGE_TYPE.discoverPage
        }
    }

    if (normalizedSegment === ROUTE_SEGMENTS.categories) {
        // Handle both "categories" and "categories/slug"
        if (cleanPath === firstSegment || cleanPath.startsWith(firstSegment + "/")) {
            return DETECTED_PAGE_TYPE.newCategoryPage
        }
    }

    if (
        normalizedSegment === ROUTE_SEGMENTS.product &&
        cleanPath.startsWith(firstSegment + "/")
    ) {
        return DETECTED_PAGE_TYPE.product
    }

    return DETECTED_PAGE_TYPE.contentPage
}
